/**
 * One shared room (2026-10-06, the user's request): whatever anyone changes in a study room,
 * everyone in it sees. The room filter, every screen's filters and drill path, where each
 * screen stands, the centre screen, the patients out for review, the ward and the host are
 * channels: a key, a way to read the local state and a way to apply someone else's.
 *
 * Ten times a second every channel is read; one whose state differs from what the room last
 * agreed on is sent (`view:set`). What arrives from others is applied and becomes the agreed
 * state, so it is not sent back. Reading rather than hooking every change means nothing that
 * changes a screen can forget to share it. The server keeps the latest value per key and
 * replays them, in order, to anyone who joins later.
 *
 * Applying must only set the channel's own state: a change that sets off other changes on
 * the receiving side would be sent again from there.
 */
import type { ViewEntry } from '@clinspace/shared';
import { VIEW_LIMITS } from '@clinspace/shared';
import type { Net } from '../app/net.ts';

export interface SyncChannel {
  /** The local state now, JSON-serialisable; round numbers so noise is not a change. */
  read(): unknown;
  /** Someone else's state for this key. */
  apply(value: unknown, from: { sessionId: string; name: string }): void;
}

const POLL_MS = 100;

export class RoomSync {
  private readonly channels = new Map<string, SyncChannel>();
  /** What the room agrees each key is, as JSON (sent by us or applied from others). */
  private readonly agreed = new Map<string, string>();
  private lastPoll = 0;
  private applying = 0;
  private readonly unsubscribe: (() => void)[] = [];
  /** Dev hooks: what was sent and applied. */
  sent = 0;
  received = 0;
  lastFrom: string | undefined;

  constructor(private readonly net: Net) {
    this.unsubscribe.push(
      net.onView((entries, from) => this.receive(entries, from)),
      // A (re)join replaces the room's state: apply it, and offer ours for what it lacks.
      net.onSnapshot(() => this.applyAll()),
    );
  }

  /** True while someone else's change is being applied. */
  get isApplying(): boolean {
    return this.applying > 0;
  }

  /** Adds a channel; the room's value for it, if any, applies at once. */
  register(key: string, channel: SyncChannel): void {
    this.channels.set(key, channel);
    const entry = this.net.view.get(key);
    if (entry) this.applyEntry(entry, { sessionId: entry.by, name: '' });
  }

  /** Stops reading a channel (a pin left the table, a screen closed). */
  unregister(key: string): void {
    this.channels.delete(key);
    this.agreed.delete(key);
  }

  has(key: string): boolean {
    return this.channels.has(key);
  }

  /** Applies everything the room holds, oldest change first; keys it lacks will be offered. */
  applyAll(): void {
    const entries = [...this.net.view.values()].sort((a, b) => a.seq - b.seq);
    const known = new Set(entries.map((e) => e.key));
    for (const key of [...this.agreed.keys()]) if (!known.has(key)) this.agreed.delete(key);
    for (const e of entries) this.applyEntry(e, { sessionId: e.by, name: '' });
  }

  /** Reads every channel (at most ten times a second) and sends what changed. */
  update(now = performance.now()): void {
    if (now - this.lastPoll < POLL_MS) return;
    this.lastPoll = now;
    // Nothing is offered before the room's state has arrived: our defaults would overwrite it.
    if (!this.net.currentRoom || !this.net.socket.connected) return;
    if (this.net.snapshotRoom !== this.net.currentRoom) return;
    const out: { key: string; value: unknown }[] = [];
    for (const [key, channel] of this.channels) {
      let json: string;
      let value: unknown;
      try {
        value = channel.read();
        json = JSON.stringify(value) ?? 'null';
      } catch (err) {
        console.warn(`[sync] could not read ${key}:`, (err as Error).message);
        continue;
      }
      if (this.agreed.get(key) === json) continue;
      this.agreed.set(key, json);
      out.push({ key, value: value === undefined ? null : value });
    }
    for (let i = 0; i < out.length; i += VIEW_LIMITS.perMessage)
      this.net.setView(out.slice(i, i + VIEW_LIMITS.perMessage));
    this.sent += out.length;
  }

  dispose(): void {
    for (const u of this.unsubscribe) u();
    this.channels.clear();
    this.agreed.clear();
  }

  private receive(entries: ViewEntry[], from: { sessionId: string; name: string }): void {
    for (const e of entries) this.applyEntry(e, from);
  }

  private applyEntry(e: ViewEntry, from: { sessionId: string; name: string }): void {
    const channel = this.channels.get(e.key);
    if (!channel || e.value === null) return;
    this.applying++;
    try {
      channel.apply(e.value, from);
      this.received++;
      this.lastFrom = from.name || this.lastFrom;
    } catch (err) {
      console.warn(`[sync] could not apply ${e.key}:`, (err as Error).message);
    } finally {
      this.applying--;
    }
    // The state after applying (it may round or clamp) is what the room agrees on.
    this.agreed.set(e.key, safeJson(channel.read()));
  }
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value) ?? 'null';
  } catch {
    return 'null';
  }
}

/** Rounds to millimetres (and thousandths) so float noise is never a change. */
export function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}
